import { useState } from 'react';
import styled from 'styled-components';
import Heading from '/components/ui/typography/Heading';
import Paragraph from '/components/ui/typography/Paragraph';
import Button from '/components/ui/buttons/Button';
import Tip from '/components/layout/Tip';
import { colors } from '/css';
import decorateText from '/lib/utils/decorateText';
import formatPrice from '/lib/utils/formatPrice';
import { JobTemplate } from '/types/jobTemplateTypes';

import dynamic from 'next/dynamic';
import InfoCard from '/components/ui/newCards/InfoCard';
import EnovaIcon from '/components/icons/EnovaIcon';
import Modal from '/components/ui/Modal';
import useEnova, { enovaDefaultSettings } from '/hooks/useEnova';
import ProductFromExternalProviderInfo, {
    Brand,
} from '../ProductFromExternalProviderInfo';

const Slider = dynamic(() => import('/components/ui/Slider'), { ssr: false });

interface Props {
    info: JobTemplate['jobTypeVersion']['info'];
    totals?: JobTemplate['totals'];
    title: string;
    onCallToActionButtonClick: () => void;
    hideStartingPrice?: boolean;
    showExternalProviderInfo?: boolean;
}

export default function JobInfo({
    info,
    totals,
    title,
    hideStartingPrice,
    onCallToActionButtonClick,
    showExternalProviderInfo = false,
}: Props) {
    const [enovaPopupVisible, setEnovaPopupVisible] = useState<boolean>(false);

    const { enovaCashbackLimit, enovaCashbackPercent } = useEnova([]);

    const enovaPopupText = () => {
        const description =
            info.enovaSupport?.description || enovaDefaultSettings.description;
        return description
            .replaceAll('{CASHBACK_LIMIT}', formatPrice(enovaCashbackLimit))
            .replaceAll(
                '{CASHBACK_PERCENT}',
                String(enovaCashbackPercent) + '%'
            );
    };

    return (
        <Wrapper>
            <Heading size="xl" as="h1" className="job-info-title mb-24">
                {title}
            </Heading>

            {totals?.startingPrice && !hideStartingPrice && (
                <Paragraph size="medium" className="mb-48 from-price">
                    Fra {formatPrice(totals.startingPrice.gross)}
                </Paragraph>
            )}

            {showExternalProviderInfo && (
                <ProductFromExternalProviderInfo
                    brand={Brand.ELIMP}
                    className="mb-48"
                />
            )}

            {info.enovaSupport?.enabled && !!enovaCashbackLimit && (
                <InfoCard variant="green" className="mb-48 flex align-center">
                    <EnovaIcon className="enova-icon" />
                    <Paragraph size="smaller">
                        Du kan få opptil{' '}
                        <strong>{formatPrice(enovaCashbackLimit)}</strong>{' '}
                        refundert med Enovastøtte.{' '}
                        <button
                            onClick={() => setEnovaPopupVisible(true)}
                            className="clickable paragraph-smaller underlined"
                        >
                            Les mer
                        </button>
                    </Paragraph>
                </InfoCard>
            )}

            {enovaPopupVisible && (
                <Modal
                    closeRequestCallback={() => setEnovaPopupVisible(false)}
                    className="p-40"
                >
                    <Heading size="medium" as="div" centered className="mb-32">
                        Enovastøtte
                    </Heading>
                    <Paragraph size="medium" centered className="mb-32">
                        {enovaPopupText()}
                    </Paragraph>
                    <div className="flex children-full-width-on-mobile full-width wrap justify-space-between">
                        <Button
                            variant="tertiary"
                            link={{ href: '/enova' }}
                            size="medium"
                            className="enova-popup-button"
                        >
                            Slik fungerer Enovastøtte
                        </Button>
                        <Button
                            variant="tertiary"
                            onClick={() => setEnovaPopupVisible(false)}
                            size="medium"
                            className="enova-popup-button"
                        >
                            Lukk
                        </Button>
                    </div>
                </Modal>
            )}

            {(info.images || []).length > 0 && (
                <Slider
                    images={info.images || []}
                    id="job-images"
                    className="mb-60"
                    bulletAlign={'flex-start'}
                />
            )}

            {info.descriptionHeading && (
                <Heading
                    size="medium"
                    as="h2"
                    className="job-info-description-title mb-12"
                >
                    {info.descriptionHeading}
                </Heading>
            )}

            {info.description && (
                <Paragraph
                    size="medium"
                    className={`${
                        info.tips ? 'mb-48' : 'mb-60'
                    } job-info-description`}
                >
                    {decorateText(info.description)}
                </Paragraph>
            )}

            <Tip text={info.tips} />

            {info.title && info.description && !info.integrationJob && (
                <div className="flex mb-60 wrap children-full-width-on-mobile">
                    <Button
                        size="medium"
                        className="mr-16"
                        onClick={onCallToActionButtonClick}
                    >
                        Kom i gang
                    </Button>
                    <Button
                        size="medium"
                        variant="tertiary"
                        link={{ href: '/nettbutikk/alle' }}
                    >
                        Se alle jobber
                    </Button>
                </div>
            )}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    .from-price {
        color: ${colors.orange};
    }
    .enova-icon {
        margin-right: 16px;
        width: 15px;
        height: 15px;
    }
    .enova-popup-button {
        min-width: calc(50% - 8px);
    }
`;
